import * as React from "react";
import {useCallback, useEffect, useState} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {
    updateConversationWithSystemMessage,
    useGuideAudioController,
    useGuidePreferences,
} from "@khan/guide-core";
import {GuideStateApi} from "@khan/guide-state";
import type {ReadingStyle as ReadingStyleType} from "@khan/guide-types";
import {
    preferredGuideLanguageChangeMessage,
    readingStyleChangeMessage,
} from "@khan/guide-utils";

import GuideLanguageSetting from "./guide-language-setting.tsx";
import GuideVoiceSetting from "./guide-voice-setting.tsx";
import ReadingStyle from "./reading-style.tsx";
import {SaveChangesButton} from "./save-changes-button.tsx";
import SuccessToast from "./success-toast.tsx";

interface Props {
    renderGuideLanguages?: boolean;
}

const KhanmigoSettingsForm = ({renderGuideLanguages = false}: Props) => {
    const {
        preferences,
        setPreferences,
        isLoading: isPreferencesLoading,
    } = useGuidePreferences();
    const {selectedVoice} = useGuideAudioController();
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [currentVoice, setCurrentVoice] = useState<string | null>(null);
    const [currentReadingStyle, setCurrentReadingStyle] =
        useState<ReadingStyleType | null>(null);
    const [currentLanguage, setCurrentLanguage] = useState<string | null>(null);

    // Update values when preferences and selectedVoice are received from hooks
    useEffect(() => {
        setCurrentVoice(selectedVoice);
        setCurrentReadingStyle(preferences.readingLevel);
        setCurrentLanguage(preferences.guideLanguageCode);
    }, [
        selectedVoice,
        preferences.readingLevel,
        preferences.guideLanguageCode,
    ]);

    const hasUnsavedChanges =
        currentVoice !== selectedVoice ||
        currentReadingStyle !== preferences.readingLevel ||
        (renderGuideLanguages &&
            currentLanguage !== preferences.guideLanguageCode);

    const handleSave = useCallback(async () => {
        setIsSaving(true);
        try {
            if (
                renderGuideLanguages &&
                currentLanguage !== preferences.guideLanguageCode
            ) {
                publishAnalyticsEventForMinorConversion({
                    eventType: "UserSettingsAiGuideLanguageChanged",
                    eventSchemaVersion: 1,
                    aiGuidePreviousLanguage: preferences.guideLanguageCode,
                    aiGuidePreviousIsDefault:
                        preferences.guideLanguageCode === "default",
                    aiGuideNewLanguage: currentLanguage || "default",
                    aiGuideNewIsDefault: currentLanguage === "default",
                });
            }

            if (currentVoice && currentVoice !== selectedVoice) {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideVoiceSelected",
                    eventSchemaVersion: 1,
                    aiGuideSelectedVoice: currentVoice,
                });
            }
            // Update new reading style info message into conversation.
            if (
                currentReadingStyle &&
                currentReadingStyle !== preferences.readingLevel
            ) {
                GuideStateApi.set("conversation", (conversation) =>
                    updateConversationWithSystemMessage(
                        conversation,
                        readingStyleChangeMessage(currentReadingStyle),
                    ),
                );
            }
            // Update new guide language info message into conversation.
            if (
                currentLanguage &&
                currentLanguage !== preferences.guideLanguageCode
            ) {
                GuideStateApi.set("conversation", (conversation) =>
                    updateConversationWithSystemMessage(
                        conversation,
                        preferredGuideLanguageChangeMessage(currentLanguage),
                    ),
                );
            }

            await setPreferences({
                guideVoiceDescriptor: currentVoice || null,
                readingLevel: currentReadingStyle,
                guideLanguageCode: renderGuideLanguages
                    ? currentLanguage || "default"
                    : preferences.guideLanguageCode,
            });
            setShowSuccessToast(true);
        } finally {
            setIsSaving(false);
        }
    }, [
        setPreferences,
        currentVoice,
        currentReadingStyle,
        currentLanguage,
        selectedVoice,
        preferences.readingLevel,
        preferences.guideLanguageCode,
        renderGuideLanguages,
    ]);

    return (
        <View style={{gap: sizing.size_080}}>
            <View style={{gap: sizing.size_320}}>
                <ReadingStyle
                    currentReadingStyle={currentReadingStyle}
                    onChange={setCurrentReadingStyle}
                    isLoading={isPreferencesLoading}
                />
                {renderGuideLanguages && (
                    <GuideLanguageSetting
                        currentLanguage={currentLanguage}
                        onChange={setCurrentLanguage}
                        isLoading={isPreferencesLoading}
                    />
                )}
                <GuideVoiceSetting
                    currentVoice={currentVoice}
                    onChange={setCurrentVoice}
                    isLoading={isPreferencesLoading}
                />
            </View>
            <SaveChangesButton
                setSettings={handleSave}
                isLoading={isSaving}
                isDisabled={
                    isPreferencesLoading || isSaving || !hasUnsavedChanges
                }
            />
            {showSuccessToast && (
                <SuccessToast onClose={() => setShowSuccessToast(false)} />
            )}
        </View>
    );
};

export default React.memo(KhanmigoSettingsForm);
